import { Injectable, inject } from '@angular/core';
import { FilterService } from 'primeng/api';

type PrimeNgFilterFunction = (value: any, filter?: any, filterLocale?: string) => boolean;

export const CUSTOM_FILTER_MATCH_MODES = {
  myTimeRangeFilter: 'mytimerangefilter',
  filterHighRisk1: 'filterHighRisk1',
  filterTvs1: 'filterTvs1',
  filterP2P3: 'filterP2P3',
  filterP2P: 'filterP2P',
  filterTvs: 'filterTvs',
  filterPendingResolutions: 'filterPendingResolutions'
} as const;

export const CUSTOM_FILTER_CRITERIA = {
  pendingResolutions: 'pendingResolutions'
} as const;

export const CUSTOM_FILTER_FIELDS = {
  resolutionCoding: 'resolutionCoding'
} as const;

const PENDING_RESOLUTIONS = [
  'Pending Waste Reconciliation',
  'Pending Assay (checking on code)',
  'Added Waste Entry'
];

const isBlankFilter = (filter: any): boolean =>
  filter === undefined ||
  filter === null ||
  filter === '' ||
  filter === 'All' ||
  (Array.isArray(filter) && filter.length === 0);

const toDate = (value: any): Date | null => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateRange = (filter: any): { startDate: Date; endDate: Date } | null => {
  const startValue = Array.isArray(filter) ? filter[0] : filter?.startDate;
  const endValue = Array.isArray(filter) ? filter[1] : filter?.endDate;
  const startDate = toDate(startValue);
  const endDate = toDate(endValue);

  if (!startDate || !endDate) {
    return null;
  }

  return { startDate, endDate };
};

const getRangeBucket = (filter: any): any => {
  if (!filter || typeof filter !== 'object' || Array.isArray(filter)) {
    return filter;
  }

  return filter.value ?? filter.hriskEvents ?? filter.tvs;
};

const getP2PBucket = (filter: any): any => {
  if (!filter || typeof filter !== 'object' || Array.isArray(filter)) {
    return filter;
  }

  return filter.value ?? filter.p2p;
};

const rangeBucketFilter = (value: any, filter: any): boolean => {
  const bucket = getRangeBucket(filter);

  if (isBlankFilter(bucket)) {
    return true;
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return false;
  }

  if (String(bucket) === '1') {
    return numericValue >= 1 && numericValue < 5;
  }

  if (String(bucket) === '2') {
    return numericValue >= 5 && numericValue < 10;
  }

  if (String(bucket) === '3') {
    return numericValue >= 10;
  }

  return true;
};

const p2pFilter = (value: any, filter: any): boolean => {
  const bucket = getP2PBucket(filter);

  if (isBlankFilter(bucket)) {
    return true;
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return false;
  }

  if (bucket === 'red') {
    return numericValue > 2;
  }

  if (bucket === 'green') {
    return numericValue < 1;
  }

  if (bucket === 'yellow') {
    return numericValue > 1 && numericValue < 2;
  }

  return true;
};

const timeRangeFilter: PrimeNgFilterFunction = (value: any, filter: any): boolean => {
  if (isBlankFilter(filter)) {
    return true;
  }

  const valueDate = toDate(value);
  const range = toDateRange(filter);

  if (!valueDate) {
    return false;
  }

  if (!range) {
    return true;
  }

  return valueDate.getTime() >= range.startDate.getTime() && valueDate.getTime() <= range.endDate.getTime();
};

const pendingResolutionsFilter: PrimeNgFilterFunction = (value: any, filter: any): boolean => {
  if (isBlankFilter(filter)) {
    return true;
  }

  return PENDING_RESOLUTIONS.includes(value);
};

const FILTER_REGISTRATIONS: [string, PrimeNgFilterFunction][] = [
  [CUSTOM_FILTER_MATCH_MODES.myTimeRangeFilter, timeRangeFilter],
  [CUSTOM_FILTER_MATCH_MODES.filterHighRisk1, rangeBucketFilter],
  [CUSTOM_FILTER_MATCH_MODES.filterTvs1, rangeBucketFilter],
  [CUSTOM_FILTER_MATCH_MODES.filterP2P3, p2pFilter],
  [CUSTOM_FILTER_MATCH_MODES.filterP2P, p2pFilter],
  [CUSTOM_FILTER_MATCH_MODES.filterTvs, rangeBucketFilter],
  [CUSTOM_FILTER_MATCH_MODES.filterPendingResolutions, pendingResolutionsFilter]
];

@Injectable({
  providedIn: 'root'
})
export class FilterRegistryService {
  private filterService = inject(FilterService);
  private registered = false;

  register(): void {
    if (this.registered) {
      return;
    }

    FILTER_REGISTRATIONS.forEach(([rule, filterFunction]) => {
      this.filterService.register(rule, filterFunction);
    });

    this.registered = true;
  }
}
